import { Input, Slider } from "@mui/material"
import { Mark } from "@mui/base"
import React, { ChangeEvent, FormEvent } from "react"
import styled, { css, ThemeProvider } from "styled-components"
import { theme } from "../../../theme"
import { CommonPopperProps } from "./CommonPopperProps"
import { Popper } from "./Popper"
import { PopperSaveButton } from "./PopperSaveButton"
import { PopperDarkTheme, PopperLightTheme } from "./popperThemes"

export type SliderPopperProps = CommonPopperProps & {
  value: number | number[] | undefined
  step: number
  min?: number
  max?: number
  marks: boolean | Mark[]
  defaultValue?: number
  formatOption?: (option: number) => string
  onChange: (newValue?: number | number[]) => void
  onSubmit: (newValue?: number | number[]) => Promise<void> | void
  disabled?: boolean
  hasInput?: boolean
  onInputChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  inputDataCy?: string
}

export const SliderPopper = (props: SliderPopperProps) => {
  const {
    popperTitle,
    value,
    step,
    min,
    max,
    marks,
    defaultValue,
    formatOption = (option) => option,
    onChange,
    onSubmit,
    onClose,
    onDelete,
    disabled = false,
    className,
    lightMode = false,
    anchorEl,
    anchorOrigin,
    transformOrigin,
    hasInput,
    onInputChange,
    inputDataCy,
    confirmText,
  } = props

  const inputRestrictions = ["e", "E", "+", "-", ".", ","]

  const handleChange = (_: Event, newValue: number | number[] | undefined) => {
    onChange(newValue)
  }

  const handleSubmit = (e: React.MouseEvent<Element, MouseEvent> | FormEvent) => {
    e.preventDefault()
    onSubmit(value)
  }

  return (
    <ThemeProvider theme={lightMode ? PopperLightTheme : PopperDarkTheme}>
      <Popper
        title={popperTitle}
        onClose={onClose}
        onDelete={onDelete}
        anchorEl={anchorEl}
        className={className}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        <SliderContainer $hasInput={hasInput}>
          <PopperSlider
            value={value}
            defaultValue={defaultValue}
            valueLabelFormat={(v) => formatOption(v)}
            marks={marks}
            step={step}
            max={max}
            min={min}
            onChange={handleChange}
            disabled={disabled}
            valueLabelDisplay="on"
            color="primary"
            $lightMode={lightMode}
            $hasInput={hasInput}
            size={"small"}
          />

          {hasInput && (
            <SliderInput
              value={value}
              defaultValue={defaultValue}
              onChange={onInputChange}
              onKeyDown={(evt) => inputRestrictions.includes(evt.key) && evt.preventDefault()}
              inputProps={{
                step: 1,
                min,
                max,
                autoComplete: "off",
                type: "number",
              }}
              data-cy={inputDataCy}
            />
          )}
        </SliderContainer>

        <PopperSaveButton onClick={handleSubmit} data-cy="sliderSaveBtn">
          {confirmText}
        </PopperSaveButton>
      </Popper>
    </ThemeProvider>
  )
}

const SliderContainer = styled.div<{ $hasInput?: boolean }>`
  ${(p) =>
    p.$hasInput
      ? css`
          padding: ${theme.spacing.lx} 0 0 ${theme.spacing.l};
        `
      : css`
          padding: ${theme.spacing.lx} ${theme.spacing.l} 0;
        `}
`

const PopperSlider = styled(Slider)<{ $lightMode?: boolean; $hasInput?: boolean }>`
  ${(p) =>
    p.$hasInput &&
    css`
      width: 80%;
    `}

  .MuiSlider-thumb {
    background: ${(p) => p.theme.primaryColor};
  }

  .MuiSlider-rail {
    color: ${theme.color.grey2};
  }

  .MuiSlider-mark:not(.MuiSlider-markActive) {
    color: ${theme.color.black4};
  }

  & .MuiSlider-valueLabel {
    background: unset;
    padding: 0;
    width: 32px;
    height: 32px;
    border-radius: 50% 50% 50% 0;
    ${(p) =>
      p.$lightMode
        ? css`
            color: ${theme.color.white};
            background-color: ${theme.color.primary};
          `
        : css`
            color: ${theme.color.primary};
            background-color: ${theme.color.white};
          `}

    transform-origin: bottom left;
    transform: translate(50%, -100%) rotate(-45deg) scale(0);
    &:before {
      display: none;
    }
    &.MuiSlider-valueLabelOpen {
      transform: translate(50%, -80%) rotate(-45deg) scale(1);
    }
    & > * {
      transform: rotate(45deg);
    }
  }

  .MuiSlider-markLabel {
    ${(p) =>
      !p.$lightMode &&
      css`
        color: ${theme.color.white};
      `}
  }
`

const SliderInput = styled(Input)`
  font-size: ${theme.fontSize.s};
  text-align: center;
  width: 45px;
  float: right;
  color: ${(p) => p.theme.color};

  .MuiInput-input {
    text-align: center;
    border-bottom: solid 1px ${theme.color.white};
    padding: 0;
  }

  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type="number"] {
    -moz-appearance: textfield;
  }
`
